import { useCallback, useEffect, useRef, useState } from 'react';
import { conversationsService } from '../../services/conversations.service';
import type { ConversationMessage } from '../../types/conversation.types';
import { extractErrorMessage } from '../../utils/error-handler';

/**
 * Owns the message thread for one conversation and drives a streaming turn.
 *
 * A turn is sent optimistically: the user bubble and an empty assistant bubble
 * appear at once, the assistant bubble fills as `delta` events arrive, and the
 * stored answer replaces it on `done`. The turn runs server-side regardless of
 * this client — aborting only stops delivery — so a fresh load always shows the
 * authoritative messages.
 */
export function useConversationStream(conversationId: string) {
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const tempSeq = useRef(0);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      // Oldest-first so the thread reads top-to-bottom.
      const res = await conversationsService.listMessages(conversationId, { page: 1, pageSize: 200, sortOrder: 'asc' });
      setMessages(res.data || []);
    } catch (e) {
      setError(extractErrorMessage(e, 'Failed to load the conversation'));
    } finally {
      setLoading(false);
    }
  }, [conversationId]);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => () => abortRef.current?.abort(), []);

  const patch = (id: string, change: Partial<ConversationMessage>) =>
    setMessages((prev) => prev.map((m) => (m._id === id ? { ...m, ...change } : m)));

  const send = useCallback(async (content: string) => {
    const text = content.trim();
    if (!text || streaming) return;
    setError(null);
    setStreaming(true);

    const n = ++tempSeq.current;
    const tempUserId = `temp-user-${n}`;
    const tempAssistantId = `temp-assistant-${n}`;
    setMessages((prev) => [
      ...prev,
      { _id: tempUserId, conversationId, turnId: `temp-${n}`, role: 'user', content: text, status: 'PENDING' },
      { _id: tempAssistantId, conversationId, turnId: `temp-${n}`, role: 'assistant', content: '', status: 'PENDING' },
    ]);

    const controller = new AbortController();
    abortRef.current = controller;
    try {
      await conversationsService.streamMessage(conversationId, { content: text }, {
        signal: controller.signal,
        onEvent: (ev) => {
          switch (ev.type) {
            case 'start':
              // Swap the optimistic user bubble for the stored one.
              setMessages((prev) => prev.map((m) => (m._id === tempUserId ? ev.userMessage : m)));
              break;
            case 'retrieval':
              patch(tempAssistantId, { retrieval: { ragCode: ev.ragCode, version: ev.version, sources: ev.sources } });
              break;
            case 'delta':
              setMessages((prev) => prev.map((m) => (m._id === tempAssistantId ? { ...m, content: m.content + ev.text } : m)));
              break;
            case 'done':
              setMessages((prev) => prev.map((m) => (m._id === tempAssistantId ? ev.message : m)));
              break;
            case 'error':
              setError(ev.message);
              patch(tempAssistantId, { status: 'FAILED', error: { code: ev.code, message: ev.message } });
              break;
          }
        },
      });
    } catch (e) {
      if (!controller.signal.aborted) {
        const msg = extractErrorMessage(e, 'The message could not be sent');
        setError(msg);
        patch(tempAssistantId, { status: 'FAILED', error: { code: 'ERR.CORE.CONVERSATION.SEND.FAILED', message: msg } });
      }
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  }, [conversationId, streaming]);

  return { messages, loading, streaming, error, send, reload };
}
